import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Bounce, toast } from "react-toastify";
import api from "../api";
import CustomSelect from "../CustomSelect";
import "./candidature.css";

const CandidatureDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [candidature, setCandidature] = useState(null);
  const [original, setOriginal] = useState(null);
  const [esito, setEsito] = useState(null);
  const [descrizioneEsito, setDescrizioneEsito] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [inputErrors, setInputErrors] = useState({});

  const esitoOptions = [
    { value: "Accettata", label: "Accettata" },
    { value: "Rifiutata da Trainer", label: "Rifiutata da Trainer" },
    { value: "Annullata da Trainer", label: "Annullata da Trainer" },
    { value: "Rifiutata da Ente", label: "Rifiutata da Ente" },
    { value: "In Lavorazione", label: "In Lavorazione" },
  ];

  const fetchCandidature = async () => {
    try {
      const res = await api.get(`/candidature/${id}`);
      if (res.status === 200) {
        setCandidature(res.data);
        setOriginal(res.data);

        const matchedEsito = esitoOptions.find(
          (opt) =>
            opt.value.toLowerCase() === (res.data.esito?.toLowerCase() || "")
        );

        if (matchedEsito) {
          setEsito(matchedEsito.value);
        } else {
          setEsito(res.data.esito || "");
        }

        setDescrizioneEsito(res.data.descrizioneEsito || "");
      } else {
        toast.error("Candidatura non trovata!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        navigate("/candidature");
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const userRole = localStorage.getItem("userRole");

    if (userRole === "TRAINER") {
      alert("Accedi come docente per modificare le Candidature!");
      navigate("/login");
      localStorage.clear();
    } else {
      fetchCandidature();
    }
  }, [navigate]);

  const isValid = () => {
    const errors = {};

    if (!esito || esito.trim().length === 0) {
      errors.esito = "Il campo Esito non può essere vuoto!";
    }

    if (!descrizioneEsito || descrizioneEsito.trim().length === 0) {
      errors.descrizioneEsito =
        "Il campo Descrizione Esito non può essere vuoto!";
    }

    setInputErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!isValid()) {
      return;
    }
    try {
      const payload = {
        esito: esito,
        descrizioneEsito: descrizioneEsito,
      };

      const res = await api.put(`/candidature/${id}`, payload);
      if (res.status === 200) {
        toast.success("Candidatura aggiornata con successo!", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
        setOriginal(candidature);
        setIsEditing(false);
        fetchCandidature();
      } else {
        toast.error("Errore durante il salvataggio", {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "light",
          transition: Bounce,
        });
      }
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  const handleCancel = () => {
    setCandidature(original);
    setEsito(original?.esito || "");
    setDescrizioneEsito(original?.descrizioneEsito || "");
    setInputErrors({});
    setIsEditing(false);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";

    const isoDatePart = dateString.slice(0, 10);
    const [year, month, day] = isoDatePart.split("-");
    if (!year || !month || !day) return "";

    return `${day}.${month}.${year}`;
  };

  const downloadTrainerCv = async () => {
    try {
      const response = await api.get("/curriculum/download", {
        params: { codiceFiscale: candidature.codiceFiscale },
        responseType: "blob",
      });

      const blob = new Blob([response.data], {
        type: response.headers["content-type"],
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const contentDisposition = response.headers["content-disposition"];
      let filename = candidature.curriculumFilename || "curriculum.pdf";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }

      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(error?.response?.data?.message, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "light",
        transition: Bounce,
      });
    }
  };

  const getEsitoColor = (esitoValue) => {
    switch (esitoValue) {
      case "Accettata":
        return "#28a745";
      case "Rifiutata da Trainer":
      case "Annullata da Trainer":
        return "#dc3545";
      case "In Lavorazione":
        return "#ffc107";
      case "Rifiutata da Ente":
      default:
        return "#6c757d";
    }
  };

  const getEsitoLabel = (esitoValue) => {
    const option = esitoOptions.find((opt) => opt.value === esitoValue);
    return option ? option.label : esitoValue;
  };

  return (
    <div className="content-page">
      {loading && (
        <div className="overlay">
          <div className="spinner"></div>
        </div>
      )}

      <div className="page-header">
        <div className="page-title">Dettagli Candidatura</div>
        <div className="header-actions">
          <button onClick={() => navigate("/candidature")} className="back-btn">
            <i className="fa-solid fa-arrow-left"></i>
            Torna alle Candidature
          </button>
        </div>
      </div>

      <div className="page-content candidature-details">
        {candidature && (
          <div className="details-container">
            {/* Header Card */}
            <div className="header-card">
              <div className="candidate-info">
                <div className="candidate-avatar">
                  <i className="fa-solid fa-user"></i>
                </div>
                <div className="candidate-details">
                  <h2 className="candidate-name">
                    {candidature.nominativoDocente}
                  </h2>
                  <div className="candidate-meta">
                    <span className="meta-item">
                      <i className="fa-solid fa-envelope"></i>
                      {candidature.email}
                    </span>
                    <span className="meta-item">
                      <i className="fa-solid fa-phone"></i>
                      {candidature.telefono}
                    </span>
                  </div>
                </div>
              </div>
              <div className="status-section">
                <div
                  className="status-badge"
                  style={{ backgroundColor: getEsitoColor(candidature.esito) }}
                >
                  {getEsitoLabel(candidature.esito)}
                </div>
                {!isEditing && (
                  <button
                    className="edit-btn"
                    onClick={() => setIsEditing(true)}
                  >
                    <i className="fa-solid fa-edit"></i>
                    Modifica
                  </button>
                )}
              </div>
            </div>

            {/* Main Content Cards */}
            <div className="content-grid">
              {/* Personal Information Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-title">
                    <i className="fa-solid fa-user-circle"></i>
                    Informazioni Personali
                  </div>
                </div>
                <div className="card-content">
                  <div className="info-grid">
                    <div className="info-item">
                      <label className="info-label">Nominativo Docente</label>
                      <div className="info-value">
                        {candidature.nominativoDocente}
                      </div>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Email</label>
                      <div className="info-value">{candidature.email}</div>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Telefono</label>
                      <div className="info-value">{candidature.telefono}</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Candidature Details Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-title">
                    <i className="fa-solid fa-file-alt"></i>
                    Dettagli Candidatura
                  </div>
                </div>
                <div className="card-content">
                  <div className="info-grid">
                    <div className="info-item">
                      <label className="info-label">Data Candidatura</label>
                      <div className="info-value">
                        {formatDate(candidature.dataCandidatura)}
                      </div>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Autore Candidatura</label>
                      <div className="info-value">
                        {candidature.autoreCandidatura}
                      </div>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Sede</label>
                      <div className="info-value">
                        {candidature.sedeIndirizzo}
                      </div>
                    </div>
                    <div className="info-item">
                      <label className="info-label">Titolo Modulo</label>
                      <div className="info-value">
                        {candidature.titoloModulo}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Esito Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-title">
                    <i className="fa-solid fa-clipboard-check"></i>
                    Esito Candidatura
                  </div>
                </div>
                <div className="card-content">
                  <div className="esito-section">
                    <div className="esito-field">
                      <label className="esito-label">Stato</label>
                      {isEditing ? (
                        <div className="edit-field">
                          <CustomSelect
                            options={esitoOptions}
                            value={
                              esitoOptions.find(
                                (opt) => opt.value === esito
                              ) ?? {
                                value: esito,
                                label: esito,
                              }
                            }
                            onChange={(selected) =>
                              setEsito(selected ? selected.value : "")
                            }
                            placeholder="Seleziona Esito"
                            className="esito-select"
                          />
                          {inputErrors.esito && (
                            <span className="error-message">
                              {inputErrors.esito}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="esito-display">
                          <div
                            className="esito-badge"
                            style={{
                              backgroundColor: getEsitoColor(candidature.esito),
                            }}
                          >
                            {getEsitoLabel(candidature.esito)}
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="esito-field">
                      <label className="esito-label">Descrizione</label>
                      {isEditing ? (
                        <div className="edit-field">
                          <textarea
                            className="esito-textarea"
                            value={descrizioneEsito}
                            onChange={(e) =>
                              setDescrizioneEsito(e.target.value)
                            }
                            placeholder="Inserisci una descrizione dell'esito..."
                          />
                          {inputErrors.descrizioneEsito && (
                            <span className="error-message">
                              {inputErrors.descrizioneEsito}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div className="esito-description">
                          {candidature.descrizioneEsito?.trim()
                            ? candidature.descrizioneEsito
                            : "Nessuna descrizione disponibile"}
                        </div>
                      )}
                    </div>

                    {candidature.dataEsito && (
                      <div className="esito-field">
                        <label className="esito-label">Data Esito</label>
                        <div className="esito-date">
                          {formatDate(candidature.dataEsito)}
                        </div>
                      </div>
                    )}

                    {candidature.autoreEsito && (
                      <div className="esito-field">
                        <label className="esito-label">Autore Esito</label>
                        <div className="esito-author">
                          {candidature.autoreEsito}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* CV Card */}
              <div className="info-card">
                <div className="card-header">
                  <div className="card-title">
                    <i className="fa-solid fa-file-pdf"></i>
                    Curriculum Vitae
                  </div>
                </div>
                <div className="card-content">
                  <div className="cv-section">
                    <div className="cv-status">
                      <div className="cv-indicator">
                        <i
                          className={`fa-solid ${
                            candidature.curriculumFilename
                              ? "fa-check-circle"
                              : "fa-times-circle"
                          }`}
                        ></i>
                        <span>
                          CV{" "}
                          {candidature.curriculumFilename
                            ? "Caricato"
                            : "Non Caricato"}
                        </span>
                      </div>
                      {candidature.curriculumFilename && (
                        <button
                          className="download-cv-btn"
                          onClick={downloadTrainerCv}
                        >
                          <i className="fa-solid fa-download"></i>
                          Scarica CV
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            {isEditing && (
              <div className="action-buttons">
                <button className="cancel-btn" onClick={handleCancel}>
                  <i className="fa-solid fa-times"></i>
                  Annulla
                </button>
                <button className="save-btn" onClick={handleSave}>
                  <i className="fa-solid fa-save"></i>
                  Salva Modifiche
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CandidatureDetails;
